/**
 * Последний снимок расчёта планирования — в браузере (IndexedDB).
 *
 * Зачем: снимок хранится на сервере и до 2026-09-20 при каждом открытии
 * страницы с планированием качался заново целиком — до 5 МБ JSON, это
 * «Загрузка данных…» на блоке по 0,5–1 с. Теперь блок сразу показывает снимок
 * из этого кэша, а у сервера спрашивает только «не пересчитывался?» —
 * присылает время расчёта (`knownCalculatedAt`), и сервер отвечает
 * `unchanged` с актуальностью, не читая и не отдавая план.
 *
 * Ключ — магазин и слот снимка (нормализованные параметры), внутри записи
 * идентичность пользователя и аккаунта: чужую запись читать нельзя, а при
 * выходе из аккаунта кэш чистится вместе с кэшем ответов таблиц.
 *
 * IndexedDB — необязательное ускорение: любая ошибка, блокировка или
 * зависание — это просто промах кэша, сеть не задерживается дольше
 * READ_TIMEOUT_MS (+ ограниченное время декодирования). Записи идут в фоне.
 * JSON кодируется/разбирается в отдельном browser worker, модели передаются
 * маленькими порциями с паузой для ввода/отрисовки. Если worker недоступен,
 * кэш пропускается: синхронного JSON-fallback на основном потоке нет.
 * Для возврата на страницу один уже разобранный снимок дополнительно живёт
 * в RAM до пяти минут (не более 8 MiB исходного JSON, это не размер JS heap).
 * Это общая ссылка без копирования тела, не кэш доступа/серверной свежести:
 * хук всё равно проверяет сервер; новые расчёты и выход сбрасывают RAM.
 */

import type { PlanParams, PlanResult } from "@/lib/supply-planning/engine";
import { decodeSupplyPlanSnapshot, encodeSupplyPlanSnapshot, stopSupplyPlanJsonCodec } from "./supply-plan-json-codec";
import { createSupplyPlanMemoryCache } from "./supply-plan-memory-cache";

const DB_NAME = "frogstat-supply-plan-snapshots";
const META = "meta";
const BODIES = "bodies";
// Открытие базы начинается при монтировании блока, параллельно с загрузкой его
// источников, и на занятой машине занимало до 3 с (стенд 2026-09-20) — лимит
// щедрый, иначе занятость превращалась в промах и полную загрузку снимка.
const OPEN_TIMEOUT_MS = 4_000;
// Тело лежит как Blob с JSON: чтение отдаёт ссылку за миллисекунды, а разбор
// идёт уже вне транзакции. Структурный клон объекта в 5 МБ читался 150–250 мс
// (замер 2026-09-20). Лимит чтения щедрый: IndexedDB работает в своём потоке,
// а обратный вызов ждёт главный поток так же, как ждал бы его ответ сети, —
// короткий лимит лишь превращал занятость страницы в промах кэша и полную
// загрузку снимка.
const READ_TIMEOUT_MS = 4_000;
const WRITE_TIMEOUT_MS = 10_000;
export const SUPPLY_PLAN_SNAPSHOT_CACHE_MAX_ENTRIES = 16;
export const SUPPLY_PLAN_SNAPSHOT_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const memory = createSupplyPlanMemoryCache(SUPPLY_PLAN_SNAPSHOT_CACHE_MAX_AGE_MS);

export type SupplyPlanSnapshotCacheMeta = {
  key: string;
  identity: string;
  storeId: string;
  calculatedAt: number;
  current: boolean;
  savedAt: number;
  usedAt: number;
};

export type CachedSupplyPlanSnapshot = SupplyPlanSnapshotCacheMeta & {
  snapshot: PlanResult & { calculatedAt: number; params?: PlanParams };
};

export function supplyPlanSnapshotCacheKey(identity: string, storeId: string, snapshotSlot: string): string {
  return `${identity}\u0000${storeId}\u0000${snapshotSlot}`;
}

/** Запись пригодна: своя идентичность, свежая, с временем расчёта. */
export function usableSupplyPlanSnapshotMeta(meta: SupplyPlanSnapshotCacheMeta | null | undefined, identity: string, now: number): meta is SupplyPlanSnapshotCacheMeta {
  return !!meta && meta.identity === identity && Number.isFinite(meta.calculatedAt) && meta.calculatedAt > 0
    && Number.isFinite(meta.savedAt) && meta.savedAt <= now && now - meta.savedAt < SUPPLY_PLAN_SNAPSHOT_CACHE_MAX_AGE_MS;
}

/** Что убрать после новой записи: просроченные и самые давно не использованные сверх лимита. */
export function supplyPlanSnapshotEvictions(entries: SupplyPlanSnapshotCacheMeta[], incomingKey: string, now: number): string[] {
  const others = entries.filter(entry => entry.key !== incomingKey);
  const usable = (entry: SupplyPlanSnapshotCacheMeta): boolean => usableSupplyPlanSnapshotMeta(entry, entry.identity, now);
  const remove = others.filter(entry => !usable(entry)).map(entry => entry.key);
  const live = others.filter(usable).sort((a, b) => a.usedAt - b.usedAt);
  let count = live.length + 1;
  for (const entry of live) {
    if (count <= SUPPLY_PLAN_SNAPSHOT_CACHE_MAX_ENTRIES) break;
    remove.push(entry.key);
    count--;
  }
  return remove;
}

let connection: Promise<IDBDatabase | null> | undefined;
// Async encoding can finish after logout: never repopulate a cleared cache.
let generation = 0;

function openCache(): Promise<IDBDatabase | null> {
  if (connection) return connection;
  connection = new Promise(resolve => {
    let settled = false;
    const finish = (db: IDBDatabase | null) => {
      if (settled) { db?.close(); return; }
      settled = true;
      clearTimeout(timer);
      resolve(db);
    };
    const timer = setTimeout(() => finish(null), OPEN_TIMEOUT_MS);
    try {
      if (typeof indexedDB === "undefined") { finish(null); return; }
      const request = indexedDB.open(DB_NAME, 1);
      request.onupgradeneeded = () => {
        if (settled) { request.transaction?.abort(); return; }
        request.result.createObjectStore(META, { keyPath: "key" });
        request.result.createObjectStore(BODIES);
      };
      request.onerror = request.onblocked = () => finish(null);
      request.onsuccess = () => {
        const db = request.result;
        db.onversionchange = () => { db.close(); connection = undefined; };
        db.onclose = () => { connection = undefined; };
        finish(db);
      };
    } catch { finish(null); }
  });
  return connection;
}

/** Результат — по commit транзакции; ошибка или тайм-аут — как промах кэша. */
async function transaction<T>(mode: IDBTransactionMode, fallback: T, timeoutMs: number, run: (tx: IDBTransaction, result: (value: T) => void) => void): Promise<T> {
  const db = await openCache();
  if (!db) return fallback;
  return new Promise(resolve => {
    let tx: IDBTransaction | undefined;
    let value = fallback;
    let done = false;
    const finish = (result: T) => { if (done) return; done = true; clearTimeout(timer); resolve(result); };
    const timer = setTimeout(() => { try { tx?.abort(); } catch {} finish(fallback); }, timeoutMs);
    try {
      tx = db.transaction([META, BODIES], mode);
      tx.oncomplete = () => finish(value);
      tx.onabort = tx.onerror = () => finish(fallback);
      run(tx, next => { value = next; });
    } catch {
      try { tx?.abort(); } catch {}
      finish(fallback);
    }
  });
}

export async function readSupplyPlanSnapshotCache(key: string, identity: string): Promise<CachedSupplyPlanSnapshot | null> {
  const startedGeneration = generation;
  const memoryVersion = memory.version;
  const now = Date.now();
  const found = await transaction<(SupplyPlanSnapshotCacheMeta & { body: Blob }) | null>("readonly", null, READ_TIMEOUT_MS, (tx, result) => {
    const meta = tx.objectStore(META).get(key);
    meta.onsuccess = () => {
      const entry = meta.result as SupplyPlanSnapshotCacheMeta | undefined;
      if (!usableSupplyPlanSnapshotMeta(entry, identity, now) || entry.key !== key) return;
      const body = tx.objectStore(BODIES).get(key);
      body.onsuccess = () => {
        if (body.result instanceof Blob && body.result.size > 0) result({ ...entry, body: body.result });
      };
    };
  });
  if (!found) return null;
  try {
    const snapshot = await decodeSupplyPlanSnapshot(found.body) as CachedSupplyPlanSnapshot["snapshot"] | null;
    if (generation !== startedGeneration) return null;
    if (!snapshot || typeof snapshot !== "object" || snapshot.calculatedAt !== found.calculatedAt) return null;
    const entry: SupplyPlanSnapshotCacheMeta = {
      key: found.key, identity: found.identity, storeId: found.storeId, calculatedAt: found.calculatedAt, current: found.current,
      savedAt: found.savedAt, usedAt: found.usedAt,
    };
    void touch(entry, now);
    const decoded = { ...entry, snapshot };
    memory.remember(decoded, found.body.size, memoryVersion);
    return decoded;
  } catch {
    return null;
  }
}

/** Navigation may reuse one decoded body. Its `current` flag is only cached
 * information: useSupplyPlanCalculation must still verify it with the server.
 * The persistent reader above deliberately always checks IDB/worker, so cache
 * recovery and diagnostics retain their existing behavior. */
export function readSupplyPlanSnapshotForNavigation(key: string, identity: string): Promise<CachedSupplyPlanSnapshot | null> {
  return Promise.resolve(memory.read(key, identity) ?? readSupplyPlanSnapshotCache(key, identity));
}

/** A successful new calculation supersedes any previously decoded body even
 * before its newly saved server snapshot is restored through IndexedDB. */
export function forgetSupplyPlanSnapshotMemory(): void {
  memory.clear();
}

function touch(entry: SupplyPlanSnapshotCacheMeta, now: number, currentOverride?: boolean): Promise<void> {
  const { key, identity, calculatedAt, savedAt } = entry;
  return transaction<void>("readwrite", undefined, WRITE_TIMEOUT_MS, tx => {
    const store = tx.objectStore(META);
    const existing = store.get(key);
    existing.onsuccess = () => {
      const latest = existing.result as SupplyPlanSnapshotCacheMeta | undefined;
      // Decode can overlap a newer save/clear. Never recreate a deleted entry
      // or overwrite newer metadata with the revision we originally read.
      if (!latest || latest.identity !== identity || latest.calculatedAt !== calculatedAt) return;
      // The in-memory successful calculation has a synthetic savedAt. A server
      // freshness mark targets calculation identity, not the IDB write time;
      // ordinary read touches additionally guard the exact persisted revision.
      if (currentOverride === undefined && latest.savedAt !== savedAt) return;
      // An ordinary read updates access time, not a freshness mark that may
      // have changed while the worker was decoding this same revision.
      store.put({ ...latest, current: currentOverride ?? latest.current, usedAt: now } satisfies SupplyPlanSnapshotCacheMeta);
    };
  });
}

/** Актуальность снимка изменилась (данные обновились) — обновляем только метку. */
export function markSupplyPlanSnapshotCache(entry: SupplyPlanSnapshotCacheMeta, current: boolean): Promise<void> {
  memory.mark(entry, current);
  return touch(entry, Date.now(), current);
}

export async function saveSupplyPlanSnapshotCache(entry: Omit<CachedSupplyPlanSnapshot, "savedAt" | "usedAt">): Promise<void> {
  const startedGeneration = generation;
  memory.clear();
  const memoryVersion = memory.version;
  if (!await openCache() || generation !== startedGeneration) return;
  const now = Date.now();
  const meta: SupplyPlanSnapshotCacheMeta = {
    key: entry.key, identity: entry.identity, storeId: entry.storeId, calculatedAt: entry.calculatedAt, current: entry.current,
    savedAt: now, usedAt: now,
  };
  const body = await encodeSupplyPlanSnapshot(entry.snapshot);
  if (!body || generation !== startedGeneration) return;
  // Do not leave an older IDB read in RAM if it completed during this save.
  if (memory.version !== memoryVersion) memory.clear();
  else memory.remember({ ...meta, snapshot: entry.snapshot }, body.size, memoryVersion);
  // Сама запись — отдельной короткой транзакцией без обратных вызовов: сразу
  // после ответа сервера главный поток на секунды занят сборкой таблицы плана,
  // и транзакция, которая ждёт свой callback (getAll → delete), не успевает
  // закоммититься до ухода со страницы — вместе с ней пропадала бы и запись.
  await transaction<void>("readwrite", undefined, WRITE_TIMEOUT_MS, tx => {
    tx.objectStore(META).put(meta);
    tx.objectStore(BODIES).put(body, meta.key);
  });
  void evict(meta.key, now);
}

/** Просроченные и самые давние сверх лимита — отдельно, после записи. */
function evict(incomingKey: string, now: number): Promise<void> {
  return transaction<void>("readwrite", undefined, WRITE_TIMEOUT_MS, tx => {
    const all = tx.objectStore(META).getAll();
    all.onsuccess = () => {
      try {
        for (const key of supplyPlanSnapshotEvictions((all.result ?? []) as SupplyPlanSnapshotCacheMeta[], incomingKey, now)) {
          tx.objectStore(META).delete(key);
          tx.objectStore(BODIES).delete(key);
        }
      } catch { try { tx.abort(); } catch {} }
    };
  });
}

/** При выходе из аккаунта — вместе с кэшем ответов таблиц. */
export function clearSupplyPlanSnapshotCache(): Promise<void> {
  generation++;
  memory.clear();
  stopSupplyPlanJsonCodec();
  return transaction<void>("readwrite", undefined, WRITE_TIMEOUT_MS, tx => {
    tx.objectStore(META).clear();
    tx.objectStore(BODIES).clear();
  });
}
